import HttpFactory from "~/repository/factory";
import {
  buildAttemptBulkResponseSubmissionPayload,
  buildAttemptResponseSubmissionPayload,
} from "~/utils/assessmentResponseSubmission";
import { buildAttemptAudioPlaybackDownloadResponse } from "~/repository/modules/assessmentAudioPlayback";
import { createPublicRequestConfig } from "~/repository/authSession";
import type { RecaptchaInput } from "~/types/auth";
import type {
  PublicAssessmentInfo,
  PublicAssessmentSession,
  PublicAssessmentResult,
  PublicAssessmentAnswer,
  PublicAssessmentAnswers,
  PublicAssessmentAnswerResult,
  PublicAssessmentAnswersResult,
  PublicAssessmentPlayback,
  PublicAssessmentContactFollowUp,
  PublicAssessmentContactRequestInput,
  PublicAssessmentStageSubmission,
} from "~/types/schoolAssessments";

const root = "/api/assessment/public/";
const anonymous = createPublicRequestConfig();

export default class PublicAssessmentModule extends HttpFactory {
  getAssessment(token: string) {
    return this.get<PublicAssessmentInfo>(
      `${root}${encodeURIComponent(token)}/`,
      {},
      anonymous,
    );
  }

  start(
    token: string,
    input: {
      full_name: string;
      email: string;
      phone: string;
      recaptcha: RecaptchaInput;
    },
  ) {
    return this.post<PublicAssessmentSession>(
      `${root}${encodeURIComponent(token)}/start/`,
      input,
      anonymous,
    );
  }

  getSession(sessionToken: string) {
    return this.get<PublicAssessmentSession>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/`,
      {},
      anonymous,
    );
  }

  submitResponse(sessionToken: string, input: PublicAssessmentAnswer) {
    return this.post<PublicAssessmentAnswerResult>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/responses/`,
      buildAttemptResponseSubmissionPayload(input),
      anonymous,
    );
  }

  submitResponsesBulk(sessionToken: string, input: PublicAssessmentAnswers) {
    return this.post<PublicAssessmentAnswersResult>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/responses/bulk/`,
      buildAttemptBulkResponseSubmissionPayload(input),
      anonymous,
    );
  }

  submit(sessionToken: string) {
    return this.post<PublicAssessmentResult>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/submit/`,
      {},
      anonymous,
    );
  }

  submitStage(sessionToken: string, input: PublicAssessmentStageSubmission) {
    return this.post<PublicAssessmentSession>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/stage-submit/`,
      input,
      anonymous,
    );
  }

  requestContact(
    sessionToken: string,
    input: PublicAssessmentContactRequestInput,
  ) {
    return this.post<PublicAssessmentContactFollowUp>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/contact-request/`,
      input,
      anonymous,
    );
  }

  startAudioPlayback(sessionToken: string, assetId: number) {
    return this.post<PublicAssessmentPlayback>(
      `${root}sessions/${encodeURIComponent(sessionToken)}/audio-playbacks/${assetId}/start/`,
      {},
      anonymous,
    );
  }

  async downloadAudioPlayback(sessionToken: string, assetId: number) {
    const playbackPath = `${root}sessions/${encodeURIComponent(sessionToken)}/audio-playbacks/${assetId}/play/`;
    const response = await this.getResponse<Blob>(
      playbackPath,
      {},
      { ...anonymous, responseType: "blob" },
    );
    return buildAttemptAudioPlaybackDownloadResponse(response);
  }
}
